import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { MdLogin, MdLogout, MdPersonOff, MdCheckCircle } from 'react-icons/md';
import Card from 'components/card/Card';
import DocumentInput from 'components/common/DocumentInput';
import { markTimeRecord } from 'services/timeRecord.service';

function getErrorMessage(error) {
  const apiMessage = error?.response?.data?.message || error?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }
  if (Array.isArray(apiMessage) && apiMessage.length > 0) {
    return apiMessage.join(', ');
  }
  return 'Verifique el documento ingresado e intente nuevamente.';
}

export default function TimeClockView() {
  const toast = useToast();
  const inputRef = useRef(null);
  const cardBg = useColorModeValue('white', 'navy.800');
  const modalBg = useColorModeValue('white', 'navy.800');
  const accentIn = useColorModeValue('green.500', 'green.300');
  const accentOut = useColorModeValue('red.500', 'red.300');

  const [document, setDocument] = useState('');
  const [markingType, setMarkingType] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const autoCloseRef = useRef(null);

  const clearAutoClose = useCallback(() => {
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }
  }, []);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const dismissSuccess = useCallback(() => {
    clearAutoClose();
    setSuccessModal(null);
    setDocument('');
    focusInput();
  }, [clearAutoClose, focusInput]);

  const dismissError = useCallback(() => {
    clearAutoClose();
    setErrorModal(null);
    focusInput();
  }, [clearAutoClose, focusInput]);

  useEffect(() => {
    focusInput();
    return () => clearAutoClose();
  }, [focusInput, clearAutoClose]);

  const handleMark = async (recordType) => {
    if (!document.trim() || markingType) return;

    try {
      setMarkingType(recordType);
      const res = await markTimeRecord({ document: document.trim(), recordType });
      const payload = res?.payload || {};
      const label = recordType === 'CHECK_IN' ? 'Entrada' : 'Salida';
      const employeeName =
        payload?.employeeName ||
        payload?.name ||
        [payload?.name, payload?.lastName].filter(Boolean).join(' ') ||
        null;

      clearAutoClose();
      setSuccessModal({
        label,
        recordType,
        message: res?.message || `${label} registrada correctamente`,
        employeeName,
      });
      autoCloseRef.current = setTimeout(() => {
        setSuccessModal(null);
        setDocument('');
        autoCloseRef.current = null;
        focusInput();
      }, 4000);
    } catch (error) {
      const message = getErrorMessage(error);
      const isNotFound =
        error?.response?.status === 404 ||
        /no se encontr|no encontrado|funcionario/i.test(message);

      if (isNotFound) {
        clearAutoClose();
        setErrorModal({ message });
        autoCloseRef.current = setTimeout(() => {
          setErrorModal(null);
          autoCloseRef.current = null;
          focusInput();
        }, 5000);
      } else {
        toast({
          title: 'Error al registrar',
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setMarkingType(null);
    }
  };

  return (
    <>
      {successModal && (
        <Box
          position="fixed"
          inset={0}
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
          onClick={dismissSuccess}
        >
          <Box
            position="absolute"
            inset={0}
            bg="blackAlpha.500"
            backdropFilter="blur(4px)"
          />
          <Box
            position="relative"
            w="100%"
            maxW="480px"
            bg={modalBg}
            border="3px solid"
            borderColor={successModal.recordType === 'CHECK_IN' ? accentIn : accentOut}
            borderRadius="2xl"
            boxShadow="2xl"
            p={{ base: 8, md: 10 }}
            textAlign="center"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-labelledby="success-title"
            aria-describedby="success-desc"
          >
            <Flex
              mx="auto"
              mb={5}
              w="88px"
              h="88px"
              borderRadius="full"
              bg={successModal.recordType === 'CHECK_IN' ? 'green.50' : 'red.50'}
              _dark={{
                bg: successModal.recordType === 'CHECK_IN' ? 'green.900' : 'red.900',
              }}
              align="center"
              justify="center"
            >
              <Icon
                as={MdCheckCircle}
                boxSize={12}
                color={successModal.recordType === 'CHECK_IN' ? accentIn : accentOut}
              />
            </Flex>
            <Heading
              id="success-title"
              size="lg"
              color={successModal.recordType === 'CHECK_IN' ? accentIn : accentOut}
              mb={3}
            >
              {successModal.label} registrada
            </Heading>
            <Text
              id="success-desc"
              fontSize="lg"
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              mb={6}
            >
              {successModal.employeeName &&
              successModal.employeeName.trim().toLowerCase() !== 'registrado'
                ? `${successModal.employeeName}. ${successModal.message}`
                : successModal.message}
            </Text>
            <Button
              colorScheme={successModal.recordType === 'CHECK_IN' ? 'green' : 'red'}
              size="lg"
              w="100%"
              onClick={dismissSuccess}
            >
              Entendido
            </Button>
          </Box>
        </Box>
      )}

      {errorModal && (
        <Box
          position="fixed"
          inset={0}
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
          onClick={dismissError}
        >
          <Box
            position="absolute"
            inset={0}
            bg="blackAlpha.500"
            backdropFilter="blur(4px)"
          />
          <Box
            position="relative"
            w="100%"
            maxW="480px"
            bg={modalBg}
            border="3px solid"
            borderColor="red.400"
            borderRadius="2xl"
            boxShadow="2xl"
            p={{ base: 8, md: 10 }}
            textAlign="center"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-labelledby="error-title"
            aria-describedby="error-desc"
          >
            <Flex
              mx="auto"
              mb={5}
              w="88px"
              h="88px"
              borderRadius="full"
              bg="red.50"
              _dark={{ bg: 'red.900' }}
              align="center"
              justify="center"
            >
              <Icon as={MdPersonOff} boxSize={12} color="red.500" />
            </Flex>
            <Heading id="error-title" size="lg" color="red.500" mb={3}>
              Usuario no encontrado
            </Heading>
            <Text
              id="error-desc"
              fontSize="lg"
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              mb={6}
            >
              {errorModal.message}
            </Text>
            <Button colorScheme="red" size="lg" w="100%" onClick={dismissError}>
              Entendido
            </Button>
          </Box>
        </Box>
      )}

      <Flex
        w="100%"
        minH={{ base: 'calc(100vh - 200px)', md: 'calc(100vh - 220px)' }}
        align="center"
        justify="center"
        px={{ base: 4, md: 6 }}
      >
        <Box maxW="560px" w="100%">
          <Card bg={cardBg} p={8}>
            <VStack spacing={6} align="stretch">
              <FormControl textAlign="center">
                <FormLabel fontSize="lg" fontWeight="600">
                  Documento
                </FormLabel>
                <DocumentInput
                  ref={inputRef}
                  size="lg"
                  fontSize="xl"
                  textAlign="center"
                  placeholder="Ingrese el número de documento"
                  value={document}
                  onChange={(e) => setDocument(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
              </FormControl>

              <HStack spacing={4}>
                <Button
                  flex={1}
                  size="lg"
                  h="80px"
                  fontSize="xl"
                  colorScheme="green"
                  leftIcon={<MdLogin />}
                  onClick={() => handleMark('CHECK_IN')}
                  isLoading={markingType === 'CHECK_IN'}
                  isDisabled={!document.trim() || !!markingType}
                  borderColor={accentIn}
                >
                  Entrada
                </Button>
                <Button
                  flex={1}
                  size="lg"
                  h="80px"
                  fontSize="xl"
                  colorScheme="red"
                  leftIcon={<MdLogout />}
                  onClick={() => handleMark('CHECK_OUT')}
                  isLoading={markingType === 'CHECK_OUT'}
                  isDisabled={!document.trim() || !!markingType}
                  borderColor={accentOut}
                >
                  Salida
                </Button>
              </HStack>
            </VStack>
          </Card>
        </Box>
      </Flex>
    </>
  );
}
